// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ProfileResponse } from "@bitebalance/contracts"
import { centsToInputString, parseEurInputToCents } from "./budgetAmount"

/**
 * Budget Model v2 MIGRATION state (redesign 2026-09-04, founder findings on
 * a real pre-v2 account). Pure — no React, no network — so the interaction
 * is testable deterministically.
 *
 * The SERVER decides `profile.budget.model`. The only way an account ever
 * migrates is ONE PATCH carrying the v2 pair
 * `{ weeklyGoalEurCents, maxCheckoutEurCents }` — produced here by
 * `setupPatchBody` from what the user configured, and sent ONLY on SAVE.
 * Nothing else in this module (Decide later, Set up, cancel, navigation,
 * reload, editing) ever yields a PATCH.
 *
 * States, derived from the server model + one per-device flag:
 *   "migrated"               model !== "legacy_hard_cap"
 *   "unclassified_expanded"  legacy model, setup card open (default)
 *   "unclassified_collapsed" legacy model, user chose "Decide later" on
 *                            this device — the compact pending notice
 *                            shows instead; the notice itself is derived
 *                            from the SERVER model, so an unclassified
 *                            account never shows nothing.
 */

export type BudgetModel = ProfileResponse["budget"]["model"]

export type MigrationState = "migrated" | "unclassified_expanded" | "unclassified_collapsed"

export interface MigrationSnapshot {
  /** `profile.budget.model` (or product-state `budgetModel`) — server truth. */
  model: BudgetModel
  /** Per-device "Decide later" flag (localStorage); never server state. */
  collapsed: boolean
}

export type MigrationEvent =
  | { type: "DECIDE_LATER" }
  | { type: "SET_UP" }
  | { type: "SAVE"; form: SetupForm }
  /** The re-fetched profile after a PATCH (or any load): the server decides the model. */
  | { type: "SERVER_MODEL"; model: BudgetModel }
  | { type: "EDIT"; form: SetupForm }
  | { type: "CANCEL" }
  | { type: "NAVIGATE" }
  | { type: "RELOAD" }

export interface SetupPatchBody {
  budget: { weeklyGoalEurCents: number | null; maxCheckoutEurCents: number | null }
}

export interface MigrationTransition {
  next: MigrationSnapshot
  /** Non-null ONLY for a valid SAVE — the exact v2 pair to PATCH. */
  patch: SetupPatchBody | null
}

export function migrationState(snapshot: MigrationSnapshot): MigrationState {
  if (snapshot.model !== "legacy_hard_cap") return "migrated"
  return snapshot.collapsed ? "unclassified_collapsed" : "unclassified_expanded"
}

export function migrationTransition(
  snapshot: MigrationSnapshot,
  event: MigrationEvent
): MigrationTransition {
  switch (event.type) {
    case "DECIDE_LATER":
      return { next: { ...snapshot, collapsed: true }, patch: null }
    case "SET_UP":
      return { next: { ...snapshot, collapsed: false }, patch: null }
    case "SAVE": {
      const patch = setupPatchBody(event.form)
      // An invalid configuration never leaves the page; the state is unchanged.
      if (!patch) return { next: snapshot, patch: null }
      return { next: { ...snapshot, collapsed: false }, patch }
    }
    case "SERVER_MODEL":
      // Once the server reports v2 the device flag is meaningless — clear it.
      return {
        next: {
          model: event.model,
          collapsed: event.model === "legacy_hard_cap" ? snapshot.collapsed : false,
        },
        patch: null,
      }
    case "EDIT":
    case "CANCEL":
    case "NAVIGATE":
    case "RELOAD":
      return { next: snapshot, patch: null }
  }
}

// ---------------------------------------------------------------- setup form

export interface SetupForm {
  /** false = "I don't want a weekly spending goal" is checked. */
  goalEnabled: boolean
  goalText: string
  /** true = "Set a maximum for one shop" is checked. */
  maxEnabled: boolean
  maxText: string
}

/** Goal PREFILLED with the previous amount (editable to any amount); maximum off and empty. */
export function initialSetupForm(previousCents: number | null, locale: "en" | "de"): SetupForm {
  return {
    goalEnabled: true,
    goalText: centsToInputString(previousCents, locale),
    maxEnabled: false,
    maxText: "",
  }
}

export interface SetupValidation {
  valid: boolean
  /** Neither a goal nor a maximum is enabled. */
  nothingChosen: boolean
  /** Enabled but the text does not parse (empty or garbage). */
  goalInvalid: boolean
  maxInvalid: boolean
  goalCents: number | null
  maxCents: number | null
}

export function setupValidation(form: SetupForm): SetupValidation {
  const goal = form.goalEnabled ? parseEurInputToCents(form.goalText) : null
  const max = form.maxEnabled ? parseEurInputToCents(form.maxText) : null
  const goalInvalid = form.goalEnabled && goal?.kind !== "cents"
  const maxInvalid = form.maxEnabled && max?.kind !== "cents"
  const nothingChosen = !form.goalEnabled && !form.maxEnabled
  return {
    valid: !nothingChosen && !goalInvalid && !maxInvalid,
    nothingChosen,
    goalInvalid,
    maxInvalid,
    goalCents: goal?.kind === "cents" ? goal.cents : null,
    maxCents: max?.kind === "cents" ? max.cents : null,
  }
}

/** The exact PATCH body for a valid form (goal-only, max-only, or both); null when invalid. */
export function setupPatchBody(form: SetupForm): SetupPatchBody | null {
  const v = setupValidation(form)
  if (!v.valid) return null
  return {
    budget: {
      weeklyGoalEurCents: form.goalEnabled ? v.goalCents : null,
      maxCheckoutEurCents: form.maxEnabled ? v.maxCents : null,
    },
  }
}

// ------------------------------------------------------ per-device flag

export const BUDGET_SETUP_COLLAPSED_KEY = "bb_budget_setup_collapsed"

/** URL hash that lands on (and expands) the setup card in Settings. */
export const BUDGET_SECTION_ID = "grocery-budget"
export const BUDGET_SETTINGS_PATH = `/app/more/preferences#${BUDGET_SECTION_ID}`

export function readCollapsedFlag(storage: Pick<Storage, "getItem"> | null = safeStorage()): boolean {
  try {
    return storage?.getItem(BUDGET_SETUP_COLLAPSED_KEY) === "1"
  } catch {
    return false
  }
}

export function writeCollapsedFlag(
  collapsed: boolean,
  storage: Pick<Storage, "setItem" | "removeItem"> | null = safeStorage()
): void {
  try {
    if (collapsed) storage?.setItem(BUDGET_SETUP_COLLAPSED_KEY, "1")
    else storage?.removeItem(BUDGET_SETUP_COLLAPSED_KEY)
  } catch {
    // Private mode / blocked storage: the card simply stays expanded.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage
  } catch {
    return null
  }
}
